import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/auth.store';
import { invalidateProjectProgress, invalidateProjectTaskViews } from './use-tasks';
import { myTasksKeys } from './use-my-tasks';
import { documentKeys } from './use-documents';
import { projectMessageKeys } from './use-project-messages';
import { invalidateProjectRecentUpdates } from './use-project-recent-updates';

/** One row of project_change_events (migration 20260828000001) — the slim,
 *  member-readable signal derived from audit_log by a database trigger.
 *  Carries only what changed, never its content. */
interface ProjectChangeEventRow {
  project_id: string;
  entity_type: string;
  entity_id: string | null;
  actor_id: string | null;
}

/** How long to wait for more events before flushing a batch of invalidations.
 *  A bulk action (multi-select, "@all") writes one audit_log row — and
 *  therefore one change event — per affected entity; without coalescing,
 *  each row would trigger its own refetch storm on every other viewer's
 *  screen. */
const FLUSH_DELAY_MS = 300;

/** How often to re-run a broad catch-up invalidation while the realtime
 *  channel is down. Coarser than the normal per-event path — this is a safety
 *  net, not the primary mechanism — so it costs little even if a screen sits
 *  disconnected for a while. */
const FALLBACK_POLL_MS = 15_000;

/** Floor between invalidations of the same key, independent of the normal
 *  FLUSH_DELAY_MS coalescing window. `project_form` autosaves every ~700 ms
 *  while a form is open (see FormView.tsx), so it is by far the noisiest
 *  entity type in production (measured: up to 22 audit_log rows/minute in a
 *  single project). Without a floor, every keystroke burst would refetch the
 *  forms list on every other viewer's screen roughly once a second. */
const MIN_FORM_REINVALIDATE_MS = 10_000;

/** Every list this hook knows how to touch, invalidated broadly. Used to
 *  catch up after a reconnect or while the fallback poll is running — cheap
 *  even when nothing changed, since TanStack only refetches queries that are
 *  currently mounted; everything else is just marked stale. */
function catchUpEverything(queryClient: QueryClient) {
  for (const key of [
    'tasks',
    'task',
    'forms',
    'form',
    // Custom form TEMPLATES are a separate query key from project form
    // INSTANCES above ('forms'/'form') and are not covered by any
    // project_change_events case (form templates are company-scoped, not
    // project-scoped, so the trigger that derives events from audit_log
    // never fires for them). Covering the key here at least means a
    // reconnect/fallback sweep picks up a template created while
    // disconnected, even though the live per-event path cannot.
    'form-templates',
    'form-template',
    'documents',
    'document',
    'notes',
    'note-comments',
    'project',
    'projects',
    'project-users',
    'project-non-members',
    'project-recent-updates',
    'project-activity',
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
  void queryClient.invalidateQueries({ queryKey: myTasksKeys.list() });
  // The unread badge is keyed per project, so there is no bare prefix to
  // invalidate here the way there is for the others — a disconnected client
  // catches it up the moment it revisits a project's Messages tab, same as
  // any other query would.
}

/**
 * Subscribes to project_change_events and invalidates the query keys the
 * change affects — the mechanism that gets another user's edit onto this
 * screen in seconds instead of on the next window focus or navigation. See
 * Documentation/Development_Log.md (2026-08-28) for the full design and why
 * `audit_log` itself cannot be subscribed to directly.
 *
 * Mount once, alongside useRealtimeNotifications — both are wired in
 * AuthGuard so every authenticated route carries them.
 */
export function useRealtimeChanges() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    // Coalescing state. Keyed by a stable string per distinct invalidation
    // action so a burst of events collapses to one call per affected key,
    // not one call per event.
    const pending = new Map<string, () => void>();
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackTimer: ReturnType<typeof setInterval> | null = null;
    let hasConnectedOnce = false;
    // Per-key last-flush timestamps, for queueThrottled below.
    const lastRunAt = new Map<string, number>();

    function queue(key: string, run: () => void) {
      pending.set(key, run);
      if (flushTimer) return;
      flushTimer = setTimeout(() => {
        flushTimer = null;
        const actions = [...pending.values()];
        pending.clear();
        for (const action of actions) action();
      }, FLUSH_DELAY_MS);
    }

    // Same coalescing as queue(), plus a floor on how often THIS key may
    // actually run, for entities that mutate far more often than any viewer
    // needs to see (see MIN_FORM_REINVALIDATE_MS). An event inside the floor
    // is dropped outright rather than deferred — the floor is what keeps a
    // busy project's screens from refetching forever, not what guarantees a
    // trailing refresh; the FALLBACK_POLL_MS sweep and the screen's own
    // staleTime already cover catching up on whatever was dropped.
    function queueThrottled(key: string, run: () => void, minIntervalMs: number) {
      const last = lastRunAt.get(key) ?? 0;
      if (Date.now() - last < minIntervalMs) return;
      queue(key, () => {
        lastRunAt.set(key, Date.now());
        run();
      });
    }

    // Realtime reports SUBSCRIBED even when the underlying table does not
    // exist — verified directly against production: subscribing to a table
    // that was never migrated in still resolves SUBSCRIBED with no error, so
    // CHANNEL_ERROR/TIMED_OUT/CLOSED cannot be trusted as the only signal
    // that the feed is down. Confirm the table is actually queryable before
    // trusting a SUBSCRIBED status and standing down the fallback poll.
    async function verifyChannelHealth() {
      const { error } = await supabase.from('project_change_events').select('id').limit(1);
      if (cancelled) return;
      if (error) {
        console.warn(
          '[realtime] project_change_events subscribed but unreadable — keeping fallback poll active',
          error,
        );
        if (!fallbackTimer) {
          fallbackTimer = setInterval(() => catchUpEverything(queryClient), FALLBACK_POLL_MS);
        }
        return;
      }
      if (fallbackTimer) {
        clearInterval(fallbackTimer);
        fallbackTimer = null;
      }
    }

    const channel = supabase
      .channel(`project-change-events-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'project_change_events' },
        (payload) => {
          const row = payload.new as ProjectChangeEventRow;

          // The acting user's own mutation already invalidated its own cache
          // via the normal onSuccess path — acting on it again here would
          // double-refetch. Trade-off, not an oversight: this user's OTHER
          // tabs and devices won't pick up their own change until their next
          // ordinary trigger (focus, mount, poll).
          if (row.actor_id === userId) return;

          applyInvalidations(row, queryClient, queue, queueThrottled);
        },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Only a RECOVERY needs a catch-up sweep; the first connect has
          // nothing to catch up on and every screen already fetches on mount.
          if (hasConnectedOnce) catchUpEverything(queryClient);
          hasConnectedOnce = true;
          // Deliberately does NOT clear fallbackTimer here — SUBSCRIBED alone
          // doesn't prove the table exists (see verifyChannelHealth above).
          // The fallback poll only stands down once that check passes.
          void verifyChannelHealth();
          return;
        }

        // CHANNEL_ERROR / TIMED_OUT / CLOSED: the push path is down. Fall
        // back to a coarse poll rather than silently reverting to today's
        // behaviour (no refresh until the next focus/mount) with no signal
        // that anything is wrong. supabase-js retries the socket on its own;
        // this only covers the gap until it reconnects.
        if (!fallbackTimer) {
          fallbackTimer = setInterval(() => catchUpEverything(queryClient), FALLBACK_POLL_MS);
        }
      });

    return () => {
      cancelled = true;
      if (flushTimer) clearTimeout(flushTimer);
      if (fallbackTimer) clearInterval(fallbackTimer);
      pending.clear();
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
}

function applyInvalidations(
  row: ProjectChangeEventRow,
  queryClient: QueryClient,
  queue: (key: string, run: () => void) => void,
  queueThrottled: (key: string, run: () => void, minIntervalMs: number) => void,
) {
  const { project_id: projectId, entity_type: entityType, entity_id: entityId } = row;

  // Every entity type touches the project's Recent Updates and Activity tabs.
  queue(`activity:${projectId}`, () => invalidateProjectRecentUpdates(queryClient, projectId));

  switch (entityType) {
    case 'task':
    case 'subtask':
    case 'task_dependency':
      queue(`tasks:${projectId}`, () =>
        queryClient.invalidateQueries({ queryKey: ['tasks', projectId] }),
      );
      // Not just the changed entity's detail: a status change, delete or link
      // edit also moves the Waiting on / Needed by panels and counts of tasks
      // linked to it, and the event can't say which those are.
      queue(`task-views:${projectId}`, () => {
        void invalidateProjectTaskViews(queryClient, projectId);
      });
      queue(`progress:${projectId}`, () => invalidateProjectProgress(queryClient, projectId));
      queue('my-tasks', () => queryClient.invalidateQueries({ queryKey: myTasksKeys.list() }));
      break;

    case 'project_form':
    case 'form_task_link':
      // Throttled, not queued plainly: autosave makes 'project_form' the
      // noisiest entity type in production (see MIN_FORM_REINVALIDATE_MS).
      // The detail invalidation that used to run here for 'project_form' is
      // deliberately gone — the editor's own screen already has the latest
      // state via the save response (FormView.tsx), so refetching the detail
      // query only ever served a bystander who has that same form's detail
      // view open passively; the list refresh below is what everyone else
      // actually needs (a form appearing, being renamed, changing status).
      queueThrottled(
        `forms:${projectId}`,
        () => queryClient.invalidateQueries({ queryKey: ['forms', projectId] }),
        MIN_FORM_REINVALIDATE_MS,
      );
      break;

    case 'document':
    case 'document_version':
    case 'document_task_share':
      queue(`documents:${projectId}`, () =>
        queryClient.invalidateQueries({ queryKey: ['documents', projectId] }),
      );
      if (entityType === 'document' && entityId) {
        queue(`document:${entityId}`, () =>
          queryClient.invalidateQueries({ queryKey: documentKeys.detail(entityId) }),
        );
      }
      break;

    case 'note':
      // The event carries the note's own id, not its task id, and notes are
      // keyed by task (['notes', taskId, filters]). Invalidating the bare
      // prefix is the honest fallback: it costs nothing for the many task
      // screens the viewer isn't looking at (TanStack only refetches active,
      // mounted queries) and still refreshes the one they are.
      queue('notes', () => queryClient.invalidateQueries({ queryKey: ['notes'] }));
      break;

    case 'note_comment':
      queue('note-comments', () => queryClient.invalidateQueries({ queryKey: ['note-comments'] }));
      break;

    case 'project':
    case 'project_member':
      queue(`progress:${projectId}`, () => invalidateProjectProgress(queryClient, projectId));
      if (entityType === 'project_member') {
        queue(`project-users:${projectId}`, () =>
          queryClient.invalidateQueries({ queryKey: ['project-users', projectId] }),
        );
        queue(`project-non-members:${projectId}`, () =>
          queryClient.invalidateQueries({ queryKey: ['project-non-members', projectId] }),
        );
      }
      break;

    case 'project_message':
      // Never invalidate the bare 'project-messages' prefix — the transcript
      // is mutated directly via setQueryData by its own 10s delta poll (see
      // use-project-messages.ts), and a refetch here would blow away that
      // merged state mid-poll. The unread badge has no poll of its own at
      // all, which is the actual fix for "a message took minutes to notice."
      queue(`messages-unread:${projectId}`, () =>
        queryClient.invalidateQueries({ queryKey: projectMessageKeys.unread(projectId) }),
      );
      break;

    default:
      // 'user' and 'role' never reach here (no project_id, see the trigger).
      // Anything else unrecognised still got its activity-feed invalidation
      // above; there is simply no more specific key to refresh for it.
      break;
  }
}
